/**
 * Dates, numbers and money — §12.4.
 *
 * Two product decisions are **enforced here rather than assumed**, because the
 * platform default is not the decision:
 *
 * * **Western digits (`0-9`), never Eastern Arabic (`٠١٢٣`).** Measured, not
 *   assumed: on this platform `ar` formats `26/07/2026` but `ar-SA` and `ar-EG`
 *   both give `٢٦‏/٠٧‏/٢٠٢٦`. Leaving it to the default makes the digits depend
 *   on which Arabic locale the browser reports. `numberingSystem: 'latn'` pins
 *   it.
 * * **The Gregorian calendar, never Hijri.** CLDR lists `islamic-umalqura` as
 *   the preferred calendar for several Arabic regions, and which one `Intl`
 *   picks varies by platform and ICU version. Pinning `calendar: 'gregory'`
 *   means the answer cannot change under us on a browser we did not test.
 *
 * Neither default is wrong in general; both are simply not what was chosen. That
 * is exactly the kind of thing that gets "fixed" back to the default by someone
 * reading the call site, which is why it lives in one module with the reason
 * attached.
 *
 * Every screen formats through here. A raw `new Intl.DateTimeFormat(...)`
 * anywhere else is a bug waiting for a Saudi user to find it.
 */

/** Pinned on every formatter in this module. */
const NUMBERING = 'latn'
const CALENDAR = 'gregory'

/**
 * Formatter construction is not free and these are called per table row, so the
 * instances are cached by their arguments.
 */
const cache = new Map<string, Intl.DateTimeFormat | Intl.NumberFormat>()

function dateFormatter(locale: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = `d:${locale}:${JSON.stringify(options)}`
  let formatter = cache.get(key) as Intl.DateTimeFormat | undefined
  if (!formatter) {
    const make = (o: Intl.DateTimeFormatOptions) =>
      new Intl.DateTimeFormat(locale, { calendar: CALENDAR, numberingSystem: NUMBERING, ...o })
    try {
      formatter = make(options)
    } catch (error) {
      // ⚠️ `timeZoneName: 'shortGeneric'` throws RangeError on Safari < 15.4,
      // Chrome < 95 and Firefox < 91. A formatter that throws at render takes
      // the whole page down for a zone label, so those engines get `short`
      // ("GMT+2") — an offset, still truthful — and nothing else changes.
      if (error instanceof RangeError && options.timeZoneName === 'shortGeneric') {
        formatter = make({ ...options, timeZoneName: 'short' })
      } else {
        throw error
      }
    }
    cache.set(key, formatter)
  }
  return formatter
}

function numberFormatter(locale: string, options: Intl.NumberFormatOptions): Intl.NumberFormat {
  const key = `n:${locale}:${JSON.stringify(options)}`
  let formatter = cache.get(key) as Intl.NumberFormat | undefined
  if (!formatter) {
    formatter = new Intl.NumberFormat(locale, { numberingSystem: NUMBERING, ...options })
    cache.set(key, formatter)
  }
  return formatter
}

/** An ISO timestamp from the API, or a `Date`. */
export type DateInput = string | number | Date

function toDate(value: DateInput): Date {
  return value instanceof Date ? value : new Date(value)
}

/**
 * `Jul 26, 2026` in English, `26‏/07‏/2026` in Arabic — Western digits,
 * Gregorian calendar.
 */
export function formatDate(value: DateInput, locale: string): string {
  return dateFormatter(locale, { dateStyle: 'medium' }).format(toDate(value))
}

export function formatDateTime(value: DateInput, locale: string): string {
  return dateFormatter(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(toDate(value))
}

/**
 * A date with its weekday — for anything a user has to turn up to.
 *
 * All timestamps from the API are UTC (§6.10). An event renders in the venue's
 * zone, which the caller passes as `timeZone`; everything else renders in the
 * reader's.
 */
export function formatEventDateTime(
  value: DateInput,
  locale: string,
  timeZone?: string,
  { zoneName = false }: { zoneName?: boolean } = {},
): string {
  // Individual components rather than `dateStyle` — `Intl` rejects the two
  // together.
  //
  // `zoneName` appends the zone the way a reader says it — "Egypt Time" /
  // "توقيت مصر" — rather than the IANA identifier. ⚠️ **`shortGeneric`, not
  // `short`:** `short` gives "GMT+2" in English and "غرينتش+2" in Arabic, an
  // offset a reader has to do arithmetic with, and flips to "+3" across
  // Egypt's DST switch so two events a week apart look like they are in two
  // places. The generic name says *whose* wall clock the time is on, which is
  // the whole point of naming it (somebody reading in Riyadh must not arrive an
  // hour late), and it stays the same all year. A zone CLDR has no generic name
  // for falls back to its offset, which is still truthful.
  return dateFormatter(locale, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    ...(timeZone ? { timeZone } : {}),
    ...(zoneName ? { timeZoneName: 'shortGeneric' as const } : {}),
  }).format(toDate(value))
}

/**
 * An event's start for a catalogue card, as two halves the card can lay out on
 * one line or two: `{ date: 'Sun, Sep 20', time: '9:00 AM Egypt Time' }` /
 * `{ date: 'الأحد، 20 سبتمبر', time: '9:00 ص توقيت مصر' }`.
 *
 * ⚠️ **Shorter than `formatEventDateTime`, never less truthful** (homepage
 * finish review, 1 October 2026). The full form — "Sun, Sep 20, 2026, 9:00 AM
 * Egypt Time" — wrapped mid-zone on a 390px card ("…Egypt / Time"). One thing
 * is cut, and only when a reader cannot need it:
 *
 * - **The year, only when it is this year** — in the venue's zone, so a Cairo
 *   event on 1 January read on 31 December elsewhere still gets its year. The
 *   weekday beside the date already pins which "Sep 20" it is; an event next
 *   year keeps its year, because "Sun, Sep 20" twelve months out is a claim
 *   about the wrong day.
 *
 * The date and time come back as separate halves rather than one string, so the
 * card can keep each unbroken and let the line break only between them (it
 * puts the comma back through its translation — `،` in Arabic).
 *
 * The zone stays, by name (`shortGeneric` — see `formatEventDateTime` for why
 * not the offset): a time with no zone beside it is how somebody arrives an
 * hour late. `now` is injectable for the tests; nothing else passes it.
 */
export function formatEventCardWhen(
  value: DateInput,
  locale: string,
  timeZone: string,
  now: DateInput = new Date(),
): { date: string; time: string } {
  const instant = toDate(value)
  const yearIn = (at: Date) => dateFormatter('en', { year: 'numeric', timeZone }).format(at)
  const sameYear = yearIn(instant) === yearIn(toDate(now))

  return {
    date: dateFormatter(locale, {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      ...(sameYear ? {} : { year: 'numeric' as const }),
      timeZone,
    }).format(instant),
    time: dateFormatter(locale, {
      hour: 'numeric',
      minute: '2-digit',
      timeZone,
      timeZoneName: 'shortGeneric',
    }).format(instant),
  }
}

/**
 * `6:00 PM` / `6:00 م` — a bare wall-clock `HH:MM` with no date attached.
 *
 * For the option labels of a time dropdown, where the value on the wire stays
 * 24-hour `HH:MM` and only the reading changes. Formatted through this module
 * like every other time in the product, so it inherits the pinned Western
 * digits — `Intl` under `ar` would otherwise be free to hand back `\u0666:\u0660\u0660 \u0645`
 * depending on which Arabic locale the browser reports.
 *
 * ⚠️ **The instant is a fiction and `timeZone: 'UTC'` is what keeps it one.**
 * There is no date here, so the components are pinned to an arbitrary day read
 * in UTC; without that, `Intl` would apply the *browser's* offset and render
 * 18:00 as 20:00 for a reader in Cairo.
 */
export function formatClock(clock: string, locale: string): string {
  const [hours = '0', minutes = '0'] = clock.split(':')
  return dateFormatter(locale, {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(2026, 0, 1, Number(hours), Number(minutes))))
}

export function formatNumber(value: number, locale: string): string {
  return numberFormatter(locale, {}).format(value)
}

/**
 * Money, from the API's `DECIMAL` string plus its currency column.
 *
 * The string is parsed here and nowhere else. Money is `DECIMAL(12,2)` on the
 * server precisely so it never passes through a float, and the only safe place
 * to spend that guarantee is at the point of display.
 */
export function formatMoney(amount: string | number, currency: string, locale: string): string {
  const value = typeof amount === 'number' ? amount : Number.parseFloat(amount)
  if (!Number.isFinite(value)) return ''

  return numberFormatter(locale, {
    style: 'currency',
    currency: currency || 'USD',
    currencyDisplay: 'code',
  }).format(value)
}

/** Seconds to `1h 24m` / `3m`. Course and lesson durations. */
export function formatDuration(seconds: number, locale: string): string {
  const total = Math.max(0, Math.round(seconds))
  const hours = Math.floor(total / 3600)
  const minutes = Math.round((total % 3600) / 60)

  // `unit` formatting rather than a hand-built "1h 24m": Arabic does not
  // abbreviate hours as "h", and `Intl` knows that and we do not.
  const parts: string[] = []
  if (hours > 0) {
    parts.push(
      numberFormatter(locale, { style: 'unit', unit: 'hour', unitDisplay: 'narrow' }).format(hours),
    )
  }
  if (minutes > 0 || hours === 0) {
    parts.push(
      numberFormatter(locale, { style: 'unit', unit: 'minute', unitDisplay: 'narrow' }).format(
        minutes,
      ),
    )
  }
  return parts.join(' ')
}
